import React, { useMemo } from "react";
import {
  Alert,
  Linking,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import { WebView } from "react-native-webview";
import { Ionicons } from "@expo/vector-icons";

import {
  buildPinTitle,
  buildPinSubtitle,
  getGoogleEmbedMapUrl,
  getGoogleMapsUrl,
  type CapturedShopPin,
} from "@/lib/location/shopLocation";

/* ================================
   HELPERS
================================ */

function parseNumber(value: string | string[] | undefined) {
  const raw = Array.isArray(value) ? value[0] : value;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
}

function parseText(value: string | string[] | undefined) {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildEmbedHtml(embedUrl: string) {
  if (!embedUrl) return "";

  const safeUrl = escapeHtml(embedUrl);

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      html, body {
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        background: #ffffff;
      }
      iframe {
        border: 0;
        width: 100%;
        height: 100%;
      }
    </style>
  </head>
  <body>
    <iframe src="${safeUrl}" loading="lazy"></iframe>
  </body>
</html>`;
}

/* ================================
   SCREEN
================================ */

export default function LocationMapScreen() {
  const params = useLocalSearchParams();

  /* ================================
     BUILD PIN (CLEAN MODEL)
  ================================ */

  const pin = useMemo<CapturedShopPin | null>(() => {
    const lat = parseNumber(params.lat);
    const lng = parseNumber(params.lng);

    if (lat == null || lng == null) return null;

    return {
      lat,
      lng,
      accuracyMeters: null,
      source: "current_location",

      landmarkName: parseText(params.landmarkName),
      roadName: parseText(params.roadName),
      areaName: parseText(params.areaName),
    };
  }, [params]);

  /* ================================
     UI TEXT
  ================================ */

  const title = useMemo(() => buildPinTitle(pin), [pin]);
  const subtitle = useMemo(() => buildPinSubtitle(pin), [pin]);

  /* ================================
     MAP URLS
  ================================ */

  const openMapUrl = useMemo(() => {
    if (!pin) return "";
    return getGoogleMapsUrl(pin.lat, pin.lng, title || "Pinned location");
  }, [pin, title]);

  const embedUrl = useMemo(() => {
    if (!pin) return "";
    return getGoogleEmbedMapUrl(pin.lat, pin.lng);
  }, [pin]);

  const embedHtml = useMemo(() => buildEmbedHtml(embedUrl), [embedUrl]);

  /* ================================
     ACTIONS
  ================================ */

  async function handleOpenGoogleMaps() {
    if (!openMapUrl) {
      Alert.alert("Unavailable", "Google Maps is not available.");
      return;
    }

    try {
      const supported = await Linking.canOpenURL(openMapUrl);

      if (!supported) {
        Alert.alert("Unavailable", "Could not open Google Maps.");
        return;
      }

      await Linking.openURL(openMapUrl);
    } catch {
      Alert.alert("Unavailable", "Could not open Google Maps.");
    }
  }

  /* ================================
     EMPTY STATE
  ================================ */

  if (!pin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centered}>
          <Text style={styles.errorTitle}>No location found</Text>
          <Text style={styles.errorText}>
            Capture location first.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  /* ================================
     UI
  ================================ */

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.content}>
        <Pressable style={styles.backRow} onPress={() => router.back()}>
          <Ionicons name="arrow-back" size={22} color="#0f172a" />
          <Text style={styles.backText}>Back</Text>
        </Pressable>

        <Text style={styles.heading}>Preview business location</Text>

        {/* ================= INFO CARD ================= */}
        <View style={styles.infoCard}>
          <View style={styles.titleRow}>
            <Ionicons name="location" size={22} color="#2563eb" />
            <Text style={styles.infoLabel}>Location preview</Text>
          </View>

          <Text style={styles.placeTitle}>{title}</Text>

          {!!subtitle && (
            <Text style={styles.subtitle}>{subtitle}</Text>
          )}

          {/* CLEAN LOCATION DETAILS */}
          {!!pin.landmarkName && (
            <Text style={styles.metaLine}>
              Landmark: {pin.landmarkName}
            </Text>
          )}

          {!!pin.roadName && (
            <Text style={styles.metaLine}>
              Road: {pin.roadName}
            </Text>
          )}

          {!!pin.areaName && (
            <Text style={styles.metaLine}>
              Area: {pin.areaName}
            </Text>
          )}
        </View>

        {/* ================= MAP ================= */}
        <View style={styles.mapCard}>
          {embedHtml ? (
            <WebView
              originWhitelist={["*"]}
              source={{ html: embedHtml }}
              style={styles.webview}
              javaScriptEnabled
              domStorageEnabled
              startInLoadingState
            />
          ) : (
            <View style={styles.mapFallback}>
              <Text style={styles.mapFallbackTitle}>
                Map preview unavailable
              </Text>
              <Text style={styles.mapFallbackText}>
                Check your Maps Embed API setup.
              </Text>
            </View>
          )}
        </View>

        {/* ================= ACTION ================= */}
        <View style={styles.actions}>
          <Pressable
            style={[styles.button, styles.primaryButton]}
            onPress={handleOpenGoogleMaps}
          >
            <Ionicons name="map" size={18} color="#fff" />
            <Text style={styles.primaryButtonText}>
              Open in Google Maps
            </Text>
          </Pressable>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

/* ================================
   STYLES
================================ */

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: "#f8fafc" },

  content: { padding: 20, gap: 16 },

  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },

  errorTitle: {
    fontSize: 22,
    fontWeight: "800",
    color: "#0f172a",
  },

  errorText: {
    fontSize: 14,
    color: "#475569",
    textAlign: "center",
  },

  backRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  backText: {
    fontSize: 16,
    fontWeight: "700",
    color: "#0f172a",
  },

  heading: {
    fontSize: 24,
    fontWeight: "800",
    color: "#0f172a",
  },

  infoCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 16,
    gap: 8,
  },

  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  infoLabel: {
    fontSize: 14,
    fontWeight: "600",
  },

  placeTitle: {
    fontSize: 20,
    fontWeight: "800",
  },

  subtitle: {
    fontSize: 14,
    color: "#475569",
  },

  metaLine: {
    fontSize: 13,
    color: "#475569",
  },

  mapCard: {
    borderRadius: 16,
    overflow: "hidden",
    height: 400,
  },

  webview: {
    flex: 1,
  },

  mapFallback: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },

  mapFallbackTitle: {
    fontSize: 16,
    fontWeight: "700",
  },

  mapFallbackText: {
    fontSize: 13,
    color: "#64748b",
  },

  actions: { marginTop: 10 },

  button: {
    height: 50,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
  },

  primaryButton: {
    backgroundColor: "#2563eb",
  },

  primaryButtonText: {
    color: "#fff",
    fontWeight: "700",
  },
});